import React, { useEffect, useState } from 'react';
import '../styles/AboutTab.css';

const AboutTab = ({ companyId, scope, apiUrl }) => {
    const [about, setAbout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        fetchAbout();
        // eslint-disable-next-line
    }, [companyId, scope]);

    const fetchAbout = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const res = await fetch(`${apiUrl}/company/about?companyId=${companyId}&scope=${scope}`);
            const data = await res.json();

            if (res.status === 202) {
                // Generation in progress
                if (retryCount < 10) {
                    setTimeout(() => {
                        setRetryCount(retryCount + 1);
                        fetchAbout();
                    }, 3000);
                } else {
                    setError('Generation is taking longer than expected. Please try again later.');
                    setLoading(false);
                }
                return;
            }

            if (!res.ok || !data.success) {
                throw new Error(data.message || 'Failed to fetch company about');
            }

            setAbout(data.data);
            setLoading(false);
            setRetryCount(0);
        } catch (err) {
            console.error('Error fetching about:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const getConfidenceBadgeClass = (confidence) => {
        switch (confidence) {
            case 'HIGH': return 'confidence-high';
            case 'MEDIUM': return 'confidence-medium';
            case 'LOW': return 'confidence-low';
            default: return 'confidence-unknown';
        }
    };

    const renderSource = (source) => {
        const icon = {
            filing: '📄',
            annual_report: '📊',
            news: '📰',
            investor_presentation: '📑',
            inference: '🤖'
        }[source.type] || '📌';

        return (
            <div key={source.title} className="source-item">
                <span className="source-icon">{icon}</span>
                <span className="source-title">{source.title}</span>
                {source.date && <span className="source-date">({source.date})</span>}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="about-loading">
                <div className="skeleton-header"></div>
                <div className="skeleton-section"></div>
                <div className="skeleton-section"></div>
                <div className="skeleton-section"></div>
                {retryCount > 0 && (
                    <div className="generation-progress">
                        Generating comprehensive company profile... (attempt {retryCount}/10)
                    </div>
                )}
            </div>
        );
    }

    if (error) {
        return (
            <div className="about-error">
                <div className="error-icon">⚠️</div>
                <div className="error-message">{error}</div>
                <button onClick={fetchAbout} className="retry-button">Retry</button>
            </div>
        );
    }

    if (!about) {
        return <div className="about-empty">No data available</div>;
    }

    return (
        <div className="about-container">
            {/* Header */}
            <div className="about-header">
                <h2>{about.name}</h2>
                <div className="about-meta">
                    <span className="as-of">As of {about.asOf}</span>
                    <span className="scope-badge">{scope}</span>
                </div>
            </div>

            {/* Main Sections */}
            <div className="about-sections">
                {about.sections.map((section) => (
                    <div key={section.id} className="section-card">
                        <div 
                            className="section-header"
                            onClick={() => toggleSection(section.id)}
                        >
                            <div className="section-title-row">
                                <h3>{section.title}</h3>
                                <span className={`confidence-badge ${getConfidenceBadgeClass(section.confidence)}`}>
                                    {section.confidence}
                                </span>
                            </div>
                            <button className="expand-toggle">
                                {expandedSections[section.id] ? '−' : '+'}
                            </button>
                        </div>
                        
                        <div className="section-summary">{section.summary}</div>
                        
                        {expandedSections[section.id] && (
                            <div className="section-details">
                                <div 
                                    className="details-markdown"
                                    dangerouslySetInnerHTML={{ __html: section.detailsMarkdown }}
                                />
                                
                                {section.keyPoints.length > 0 && (
                                    <div className="key-points">
                                        <h4>Key Points:</h4>
                                        <ul>
                                            {section.keyPoints.map((point, idx) => (
                                                <li key={idx}>{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {section.sources.length > 0 && (
                                    <div className="sources">
                                        <h4>Sources:</h4>
                                        <div className="sources-list">
                                            {section.sources.map(renderSource)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Structured Data Sections */}
            <div className="structured-sections">
                {/* Segment Breakdown */}
                {about.segmentBreakdown.segments.length > 0 && (
                    <div className="data-card">
                        <div className="card-header">
                            <h3>Business Segments</h3>
                            <span className={`confidence-badge ${getConfidenceBadgeClass(about.segmentBreakdown.confidence)}`}>
                                {about.segmentBreakdown.confidence}
                            </span>
                        </div>
                        <div className="segments-grid">
                            {about.segmentBreakdown.segments.map((seg, idx) => (
                                <div key={idx} className="segment-item">
                                    <div className="segment-name">{seg.name}</div>
                                    {seg.revenueSharePct !== null && (
                                        <div className="segment-share">{seg.revenueSharePct.toFixed(1)}%</div>
                                    )}
                                    {seg.notes && <div className="segment-notes">{seg.notes}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Geography */}
                {about.geography.byCountry.length > 0 && (
                    <div className="data-card">
                        <div className="card-header">
                            <h3>Geographic Distribution</h3>
                            <span className={`confidence-badge ${getConfidenceBadgeClass(about.geography.confidence)}`}>
                                {about.geography.confidence}
                            </span>
                        </div>
                        <div className="geography-split">
                            {about.geography.domesticRevenuePct !== null && (
                                <div className="geo-item">
                                    <span className="geo-label">Domestic</span>
                                    <span className="geo-value">{about.geography.domesticRevenuePct.toFixed(1)}%</span>
                                </div>
                            )}
                            {about.geography.exportRevenuePct !== null && (
                                <div className="geo-item">
                                    <span className="geo-label">Export</span>
                                    <span className="geo-value">{about.geography.exportRevenuePct.toFixed(1)}%</span>
                                </div>
                            )}
                        </div>
                        <div className="country-breakdown">
                            {about.geography.byCountry.map((country, idx) => (
                                <div key={idx} className="country-item">
                                    <span>{country.country}</span>
                                    {country.revenuePct !== null && <span>{country.revenuePct.toFixed(1)}%</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Competition */}
                {about.competition.keyCompetitors.length > 0 && (
                    <div className="data-card">
                        <div className="card-header">
                            <h3>Competition & Market Position</h3>
                            <span className={`confidence-badge ${getConfidenceBadgeClass(about.competition.confidence)}`}>
                                {about.competition.confidence}
                            </span>
                        </div>
                        <div className="competitors-list">
                            {about.competition.keyCompetitors.map((comp, idx) => (
                                <div key={idx} className="competitor-item">
                                    <span className="competitor-name">{comp.name}</span>
                                    {comp.notes && <span className="competitor-notes">{comp.notes}</span>}
                                </div>
                            ))}
                        </div>
                        {about.competition.barriersToEntry.length > 0 && (
                            <div className="barriers-section">
                                <h4>Barriers to Entry:</h4>
                                <ul>
                                    {about.competition.barriersToEntry.map((barrier, idx) => (
                                        <li key={idx}>
                                            <strong>{barrier.type}:</strong> {barrier.detail}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Cyclicality */}
                <div className="data-card">
                    <div className="card-header">
                        <h3>Cyclicality Assessment</h3>
                        <span className={`confidence-badge ${getConfidenceBadgeClass(about.cyclicality.confidence)}`}>
                            {about.cyclicality.confidence}
                        </span>
                    </div>
                    <div className="cyclicality-badge">
                        {about.cyclicality.classification.replace('_', ' ').toUpperCase()}
                    </div>
                    {about.cyclicality.drivers.length > 0 && (
                        <div className="drivers-section">
                            <h4>Key Drivers:</h4>
                            <ul>
                                {about.cyclicality.drivers.map((driver, idx) => (
                                    <li key={idx}>{driver}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {about.cyclicality.whatToMonitor.length > 0 && (
                        <div className="monitor-section">
                            <h4>What to Monitor:</h4>
                            <ul>
                                {about.cyclicality.whatToMonitor.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Price Drivers */}
                <div className="data-card">
                    <div className="card-header">
                        <h3>Price Drivers & Catalysts</h3>
                        <span className={`confidence-badge ${getConfidenceBadgeClass(about.priceDrivers.confidence)}`}>
                            {about.priceDrivers.confidence}
                        </span>
                    </div>
                    <div className="catalysts-grid">
                        <div className="catalysts-column positive">
                            <h4>Positive Catalysts</h4>
                            <ul>
                                {about.priceDrivers.positiveCatalysts.map((cat, idx) => (
                                    <li key={idx}>{cat}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="catalysts-column negative">
                            <h4>Negative Catalysts</h4>
                            <ul>
                                {about.priceDrivers.negativeCatalysts.map((cat, idx) => (
                                    <li key={idx}>{cat}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {about.priceDrivers.keyRisks.length > 0 && (
                        <div className="risks-section">
                            <h4>Key Risks:</h4>
                            <div className="risk-tags">
                                {about.priceDrivers.keyRisks.map((risk, idx) => (
                                    <span key={idx} className="risk-tag">{risk}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Governance */}
                <div className="data-card">
                    <div className="card-header">
                        <h3>Governance & Capital Allocation</h3>
                        <span className={`confidence-badge ${getConfidenceBadgeClass(about.governance.confidence)}`}>
                            {about.governance.confidence}
                        </span>
                    </div>
                    <div className="governance-section">
                        <div className="promoter-info">
                            {about.governance.promoters.holdingPct !== null && (
                                <div className="info-row">
                                    <span className="label">Promoter Holding:</span>
                                    <span className="value">{about.governance.promoters.holdingPct.toFixed(2)}%</span>
                                </div>
                            )}
                            {about.governance.promoters.pledgePct !== null && (
                                <div className="info-row">
                                    <span className="label">Pledge:</span>
                                    <span className="value">{about.governance.promoters.pledgePct.toFixed(2)}%</span>
                                </div>
                            )}
                        </div>
                        {about.governance.redFlags.length > 0 && (
                            <div className="red-flags">
                                <h4>⚠️ Red Flags:</h4>
                                <ul>
                                    {about.governance.redFlags.map((flag, idx) => (
                                        <li key={idx}>{flag}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Developments */}
            {about.recentDevelopments && (
                <div className="recent-developments">
                    <h3>Recent Developments</h3>
                    <div className="developments-summary">
                        {about.recentDevelopments.summary}
                    </div>
                    {about.recentDevelopments.news.length > 0 && (
                        <div className="recent-news-list">
                            {about.recentDevelopments.news.map((news, idx) => (
                                <div key={idx} className="news-item-compact">
                                    <a href={news.url} target="_blank" rel="noopener noreferrer">
                                        {news.title}
                                    </a>
                                    <span className="news-date">{news.publishedAt}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AboutTab;